'use client';

import { RefObject, useEffect, useRef, useState } from 'react';

/**
 * Custom hook for creating a parallax effect on a DOM element.
 *
 * @template T - The type of the DOM element.
 * @param {number} speed - The speed of the parallax effect. Default value is 0.2.
 * @returns {Object} - An object containing the DOM element reference and the parallax style.
 */
export const useParallax = <T extends HTMLElement>(
  speed: number = 0.2,
): {
  ref: RefObject<T | null>;
  parallaxStyle: {
    transform: string;
  };
} => {
  const ref = useRef<T>(null);
  const [offsetY, setOffsetY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (ref.current) {
        const elementTop = ref.current.offsetTop;
        const elementHeight = ref.current.offsetHeight;
        const windowHeight = window.innerHeight;
        const scrollY = window.scrollY;

        // Check if the element is in the viewport
        if (scrollY + windowHeight > elementTop && scrollY < elementTop + elementHeight) {
          const offset = scrollY - elementTop + windowHeight / 2; // Center of the viewport
          requestAnimationFrame(() => {
            setOffsetY(-offset * speed);
          });
        }
      }
    };
    // const handleScroll = () => {
    //   if (ref.current) {
    //     const elementTop = ref.current.offsetTop;
    //     const elementHeight = ref.current.offsetHeight;
    //     const windowHeight = window.innerHeight;
    //     const scrollY = window.scrollY;

    //     // Calculate element's position relative to viewport
    //     const elementBottom = elementTop + elementHeight;
    //     const viewportTop = scrollY;
    //     const viewportBottom = scrollY + windowHeight;

    //     // Calculate how far the element has moved through the viewport
    //     // This should be 0 when element enters viewport, and increase as it moves through
    //     const elementRelativeToViewport = viewportBottom - elementTop;
    //     const viewportTravelDistance = windowHeight + elementHeight;

    //     // Normalize to 0-1 range based on viewport travel
    //     const normalizedProgress = Math.max(0, Math.min(1, elementRelativeToViewport / viewportTravelDistance));

    //     // Calculate parallax offset - this keeps the movement consistent regardless of page position
    //     const parallaxOffset = -(normalizedProgress * viewportTravelDistance * speed);

    //     // Only update if the element is reasonably close to the viewport for performance
    //     const buffer = windowHeight;
    //     const isNearViewport = viewportBottom + buffer > elementTop && viewportTop < elementBottom + buffer;

    //     if (isNearViewport) {
    //       requestAnimationFrame(() => {
    //         setOffsetY(parallaxOffset);
    //       });
    //     }
    //   }
    // };
    const checkAndUpdatePosition = () => handleScroll();

    window.addEventListener('scroll', checkAndUpdatePosition, {
      passive: true,
    });
    window.addEventListener('resize', checkAndUpdatePosition);

    // Initial position update on mount
    checkAndUpdatePosition();

    return () => {
      window.removeEventListener('scroll', checkAndUpdatePosition);
      window.removeEventListener('resize', checkAndUpdatePosition);
    };
  }, [speed]);

  if (speed === 0 || speed > 1) {
    return { ref, parallaxStyle: { transform: 'translateY(0px)' } };
  }

  const parallaxStyle = {
    transform: `translateY(${offsetY}px)`,
  };

  return { ref, parallaxStyle };
};
